/**
 * adminPrimitives.jsx
 *
 * Small shared building blocks for the Admin Panel redesign — kept here so the
 * accordion, pagination and search controls have ONE implementation reused
 * across the incident editor, the tag list and the PIN list (no duplication).
 */
import React, { useState, useId, useRef, useEffect } from 'react';
import { buildPageSequence } from '../utils/pagination.js';

export { buildPageSequence } from '../utils/pagination.js';

/** Segmented filter chip — shared by Access log and Users panels. */
export function AdminFilterChip({ active, onClick, children }) {
  return (
    <button
      type="button"
      className={`btn sm${active ? ' primary' : ' ghost'}`}
      onClick={onClick}
      style={{ fontSize: 11, padding: '5px 10px' }}
    >
      {children}
    </button>
  );
}

const Chevron = () => (
  <svg className="acc-chevron" width="14" height="14" viewBox="0 0 24 24" fill="none"
       stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
       aria-hidden="true">
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

/** Stable slug for tab / tabpanel id wiring. */
export function tabSlug(label) {
  return String(label).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

/**
 * Keyboard-navigable admin tab bar — roving tabindex, Arrow/Home/End, horizontal
 * scroll on narrow viewports (no row wrap).
 */
export function AdminTabBar({ tabs, active, onChange }) {
  const listRef = useRef(null);
  const tabRefs = useRef([]);
  const [scrollable, setScrollable] = useState(false);

  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    const check = () => setScrollable(el.scrollWidth > el.clientWidth + 1);
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, [tabs]);

  function focusTab(index) {
    const el = tabRefs.current[index];
    el?.focus();
    el?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }

  function selectTab(index) {
    if (index < 0 || index >= tabs.length) return;
    onChange(tabs[index]);
    requestAnimationFrame(() => focusTab(index));
  }

  function handleKeyDown(e) {
    const i = tabs.indexOf(active);
    if (i < 0) return;
    let next = i;
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      next = Math.min(i + 1, tabs.length - 1);
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      next = Math.max(i - 1, 0);
    } else if (e.key === 'Home') {
      e.preventDefault();
      next = 0;
    } else if (e.key === 'End') {
      e.preventDefault();
      next = tabs.length - 1;
    } else {
      return;
    }
    if (next !== i) selectTab(next);
  }

  return (
    <div className={`admin-tablist-wrap${scrollable ? ' admin-tablist-wrap--scrollable' : ''}`}>
      <div
        ref={listRef}
        role="tablist"
        aria-label="Admin sections"
        className="admin-tablist"
        onKeyDown={handleKeyDown}
      >
        {tabs.map((tab, i) => {
          const slug = tabSlug(tab);
          const selected = active === tab;
          return (
            <button
              key={tab}
              ref={el => { tabRefs.current[i] = el; }}
              type="button"
              role="tab"
              id={`admin-tab-${slug}`}
              aria-selected={selected}
              aria-controls={`admin-tabpanel-${slug}`}
              tabIndex={selected ? 0 : -1}
              className={`admin-tab btn ghost${selected ? ' admin-tab--active' : ''}`}
              onClick={() => onChange(tab)}
            >
              {tab}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Controlled accordion item. The body is rendered LAZILY — children are only
 * mounted while `open` is true, so a list of N items never builds N heavy bodies
 * at once. `meta` shows a count/summary on the collapsed header.
 */
export function AccordionItem({ title, meta, open, onToggle, children }) {
  const bodyId = useId();
  return (
    <div className="acc-item">
      <button
        type="button"
        className="acc-head"
        aria-expanded={open}
        aria-controls={bodyId}
        onClick={onToggle}
      >
        <Chevron />
        <span className="acc-title">{title}</span>
        {meta != null && <span className="acc-meta">{meta}</span>}
      </button>
      {open && (
        <div className="acc-body" id={bodyId} role="region">
          {children}
        </div>
      )}
    </div>
  );
}

/**
 * Search box for any list >~10 items. Semantic <search>-style input with a label.
 */
export function ListSearch({ value, onChange, placeholder = 'Search…', label }) {
  const id = useId();
  return (
    <div style={{ marginBottom: 10 }}>
      {label && <label htmlFor={id} className="sr-only" style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>{label}</label>}
      <input
        id={id}
        type="search"
        className="input"
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        aria-label={label || placeholder}
        style={{ width: '100%', fontSize: 12 }}
      />
    </div>
  );
}

/**
 * Pagination bar with a "Showing X–Y of N" count and numbered pages (sliding
 * window + first/last). Pure presentational — the caller owns `page` state.
 */
export function Pager({ page, pageCount, total, from, to, onPage, unit = 'items' }) {
  if (total === 0) return null;
  const sequence = buildPageSequence(page, pageCount);

  return (
    <nav className="pager" aria-label="Pagination">
      <span className="pager-count">
        Showing {from}–{to} of {total} {unit}
      </span>
      <span className="pager-spacer" />
      {pageCount > 1 && (
        <div className="page-btns">
          <button
            type="button"
            className="page-btn"
            onClick={() => onPage(page - 1)}
            disabled={page <= 1}
            aria-label="Previous page"
          >
            ‹
          </button>
          {sequence.map((token, i) =>
            token === 'ellipsis' ? (
              <span key={`e-${i}`} className="pager-ellipsis" aria-hidden="true">…</span>
            ) : (
              <button
                key={token}
                type="button"
                className={`page-btn${token === page ? ' active' : ''}`}
                onClick={() => onPage(token)}
                aria-label={`Page ${token}`}
                aria-current={token === page ? 'page' : undefined}
              >
                {token}
              </button>
            ),
          )}
          <button
            type="button"
            className="page-btn"
            onClick={() => onPage(page + 1)}
            disabled={page >= pageCount}
            aria-label="Next page"
          >
            ›
          </button>
        </div>
      )}
    </nav>
  );
}

/**
 * Tiny hook: bounded pagination over an already-filtered array. Clamps the page
 * if the filter shrinks the list under the current page.
 */
export function usePagination(items, pageSize = 25) {
  const [page, setPage] = useState(1);
  const total = items.length;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(page, pageCount);
  const start = (safePage - 1) * pageSize;
  const slice = items.slice(start, start + pageSize);
  return {
    page: safePage,
    pageCount,
    total,
    from: total === 0 ? 0 : start + 1,
    to: Math.min(start + pageSize, total),
    slice,
    setPage,
  };
}
